import React, { useRef, useState } from "react";
import { ImagePlus, Loader2, Upload, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

// UploadPhotoButton — uploads photos from the agent's computer to Emergent
// object storage and hands back the URLs to store on the trip. Photos are
// downscaled server-side to 2000px so the client's page stays fast
// (2026-09-23; agents could previously only paste a URL).
export function UploadPhotoButton({
  onUploaded,
  multiple = true,
  className = "",
  title = "Subir foto del ordenador",
  testid = "upload-photo",
  children,
}) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(0);

  const pick = async (e) => {
    const files = [...(e.target.files || [])];
    e.target.value = "";  // let the same file be picked again later
    if (!files.length) return;
    setBusy(files.length);
    const urls = [];
    for (const f of files) {
      const body = new FormData();
      body.append("file", f);
      try {
        const { data } = await api.post("/uploads/image", body, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 120000,
        });
        urls.push(data.url);
      } catch (err) {
        toast.error(err?.response?.data?.detail || `No he podido subir ${f.name}`);
      }
      setBusy((n) => n - 1);
    }
    if (urls.length) {
      onUploaded(urls);
      toast.success(urls.length === 1 ? "Foto subida" : `${urls.length} fotos subidas`);
    }
  };

  return (
    <>
      <input
        ref={inputRef} type="file" accept="image/*" multiple={multiple}
        onChange={pick} className="hidden" data-testid={`${testid}-input`}
      />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={busy > 0}
        title={title}
        data-testid={testid}
        className={className || "inline-flex items-center gap-1.5 text-[12px] px-2 py-1 border border-clay-300 rounded hover:border-terracotta hover:text-terracotta disabled:opacity-60"}
      >
        {busy > 0
          ? <><Loader2 size={12} className="animate-spin"/> Subiendo{busy > 1 ? ` (${busy})` : ""}…</>
          : (children || <><Upload size={12}/> Subir</>)}
      </button>
    </>
  );
}

// PhotoStrip — thumbnails + upload/URL tile. Drag a thumbnail onto another
// to reorder; the first one is the cover on the client's page.
export function PhotoStrip({ photos, onChange, testid }) {
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);

  const move = (from, to) => {
    if (from == null || Number.isNaN(from) || from === to) return;
    const next = [...photos];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onChange(next);
  };

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid={testid}>
      {photos.map((u, i) => (
        <div
          key={`${u}-${i}`}
          draggable
          onDragStart={(e) => {
            e.dataTransfer.effectAllowed = "move";
            e.dataTransfer.setData("text/plain", String(i));
            setDragIdx(i);
          }}
          onDragEnd={() => { setDragIdx(null); setOverIdx(null); }}
          onDragOver={(e) => { e.preventDefault(); if (overIdx !== i) setOverIdx(i); }}
          onDragLeave={() => setOverIdx((cur) => (cur === i ? null : cur))}
          onDrop={(e) => {
            e.preventDefault();
            move(Number(e.dataTransfer.getData("text/plain")), i);
            setDragIdx(null);
            setOverIdx(null);
          }}
          data-testid={`${testid}-photo-${i}`}
          title={i === 0 ? "Portada · arrastra para reordenar" : "Arrastra para reordenar"}
          className={`relative cursor-grab active:cursor-grabbing transition-opacity ${
            dragIdx === i ? "opacity-40" : ""
          } ${overIdx === i && dragIdx !== i ? "ring-2 ring-terracotta rounded" : ""}`}
        >
          <img src={u} alt="" loading="lazy" draggable={false}
               className="w-16 h-12 object-cover rounded border border-clay-200 pointer-events-none"/>
          {i === 0 && (
            <span className="absolute bottom-0 left-0 right-0 bg-clay-900/75 text-white text-[8px] leading-[11px] text-center tracking-wide rounded-b">
              PORTADA
            </span>
          )}
          <button
            onClick={() => onChange(photos.filter((_, j) => j !== i))}
            title="Quitar foto"
            data-testid={`${testid}-remove-${i}`}
            className="absolute -top-1.5 -right-1.5 bg-white border border-clay-300 rounded-full p-0.5 text-clay-500 hover:text-terracotta"
          >
            <X size={10}/>
          </button>
        </div>
      ))}
      <PhotoDropzone
        testid={testid}
        onUploaded={(urls) => onChange([...photos, ...urls])}
        onPasteUrl={(u) => onChange([...photos, u])}
      />
      {photos.length > 1 && (
        <span className="text-[10px] text-clay-400">arrastra para reordenar</span>
      )}
    </div>
  );
}

// PhotoDropzone — the empty-state tile: click to upload, or switch to the
// "paste a URL" input (Travefy photos still arrive as URLs).
export function PhotoDropzone({ onUploaded, onPasteUrl, testid }) {
  const [mode, setMode] = useState("idle");
  const [url, setUrl] = useState("");
  const add = () => {
    const u = url.trim();
    if (!u) return;
    onPasteUrl(u);
    setUrl("");
    setMode("idle");
  };

  if (mode === "url") {
    return (
      <div className="flex items-center gap-1">
        <input
          autoFocus type="url" value={url}
          onChange={(e) => setUrl(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") add(); if (e.key === "Escape") setMode("idle"); }}
          placeholder="https://…"
          data-testid={`${testid}-url-input`}
          className="text-[12px] border border-clay-300 rounded px-2 py-1 w-56 focus:outline-none focus:ring-1 focus:ring-terracotta"
        />
        <button onClick={add} className="text-[12px] px-2 py-1 bg-clay-900 text-white rounded" data-testid={`${testid}-url-save`}>
          Añadir
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <UploadPhotoButton
        onUploaded={onUploaded}
        testid={`${testid}-upload`}
        className="w-20 h-12 border border-dashed border-clay-300 rounded text-clay-500 hover:border-terracotta hover:text-terracotta inline-flex flex-col items-center justify-center gap-1 disabled:opacity-60"
        title="Subir foto del ordenador"
      >
        <ImagePlus size={14}/>
        <span className="text-[10px] leading-none">Subir</span>
      </UploadPhotoButton>
      <button
        type="button"
        onClick={() => setMode("url")}
        data-testid={`${testid}-add`}
        title="Añadir foto pegando una URL"
        className="text-[11px] text-clay-500 hover:text-terracotta underline decoration-dotted"
      >
        o pegar URL
      </button>
    </div>
  );
}
